/**
 * Camera bookmarks (doc 147 §5.3 — 3D evolution PR 3).
 *
 * Four per-user slots persist to `/users/{uid}/atlasBookmarks/{slot}` so the
 * camera state survives across devices. Pure helpers below are the schema
 * boundary — every read from Firestore goes through {@link normalizeBookmark}
 * so an untrusted doc can't crash the renderer.
 *
 * Slot strings (`'1' | '2' | '3' | '4'`) intentionally overlap with the
 * existing `1`..`9` domain-focus keys in `keyboardMap.ts`. The dispatcher
 * disambiguates by modifier (plain `1` → focus domain; `b` then `1` →
 * bookmark restore — wiring lands in 3D-PR2).
 */

/** Serialisable camera state — used by bookmarks and keyboard framing. */
export interface CameraBookmark {
  /** World-space camera position (x, y, z). */
  position: [number, number, number];
  /** Look-at target (x, y, z). */
  target: [number, number, number];
  /** Orthographic zoom for 2D; logical zoom for 3D dolly. */
  zoom: number;
}

/** Manifest-scale inputs used to size the initial camera distance. */
export interface CameraFrameInput {
  /** Total domain count — the Y-stack height scales with this (see layoutZ.ts PLANE_SEPARATION). */
  domainCount: number;
  /** The largest per-domain entity count — the in-plane ring radius scales with this (see layoutXY.ts). */
  maxDomainEntityCount: number;
}

/** The original fixed pose — elevated, offset-right, looking down at origin. */
const BASE_POSITION: [number, number, number] = [400, 500, 800];
/** Domain count the original fixed pose was tuned to fit comfortably. */
const BASE_DOMAIN_COUNT = 8;
/**
 * Per-domain entity count the original fixed pose assumes as "typical" —
 * exported so `layoutXY.ts`'s in-plane radius cap can scale off the exact
 * same baseline (see `domainRadiusScale`), keeping the camera pull-back and
 * the node cloud's actual footprint in lockstep.
 */
export const BASE_DOMAIN_ENTITY_COUNT = 20;
const BASE_DISTANCE = Math.hypot(...BASE_POSITION);
/** Keep the scaled camera comfortably inside OrbitControls' maxDistance (2400). */
const MAX_SAFE_DISTANCE = 2400 * 0.95;

/**
 * Initial 3D camera — elevated, offset-right, looking down at origin.
 * Domains stack along world Y; this pose makes the stack visible as
 * horizontal strata rather than edge-on bands.
 *
 * Called with no argument, returns the original fixed pose unchanged
 * (small manifests keep their existing framing exactly). Called with a
 * {@link CameraFrameInput}, scales the distance up for manifests with more
 * domains (taller Y-stack) or bigger individual domains (wider sunflower
 * ring), clamped so it never exceeds OrbitControls' maxDistance.
 */
export function defaultCameraBookmark(input?: CameraFrameInput): CameraBookmark {
  const scale = cameraDistanceScale(input);
  return {
    position: [BASE_POSITION[0] * scale, BASE_POSITION[1] * scale, BASE_POSITION[2] * scale],
    target: [0, 0, 0],
    zoom: 1,
  };
}

/**
 * The same distance multiplier {@link defaultCameraBookmark} applies to
 * `BASE_POSITION` — exposed so consumers that scale alongside the camera
 * (e.g. `ConstellationCanvas3d`'s fog distances, which must stay past
 * wherever the camera ends up) can derive their own scaled values from the
 * identical formula rather than recomputing it from `position`.
 */
export function cameraDistanceScale(input?: CameraFrameInput): number {
  if (!input) return 1;
  const domainScale = Math.max(1, input.domainCount / BASE_DOMAIN_COUNT);
  const ringScale = Math.max(1, Math.sqrt(input.maxDomainEntityCount / BASE_DOMAIN_ENTITY_COUNT));
  const maxScale = MAX_SAFE_DISTANCE / BASE_DISTANCE;
  return Math.min(Math.max(domainScale, ringScale), maxScale);
}

export type BookmarkSlot = '1' | '2' | '3' | '4';

export const bookmarkSlots: BookmarkSlot[] = ['1', '2', '3', '4'];

export function isBookmarkSlot(value: string): value is BookmarkSlot {
  return (bookmarkSlots as readonly string[]).includes(value);
}

/**
 * Returns the canonical Firestore segments for a bookmark doc. The multi-
 * segment form is the safe boundary — the Firestore SDK rejects `/` inside
 * any single segment, so even if `uid` is attacker-controlled the path
 * cannot escape the per-user subtree.
 *
 * (Firestore rules in `firestore.rules` are the primary defence; this is
 * defence-in-depth.)
 */
export function bookmarkDocSegments(
  uid: string,
  slot: BookmarkSlot,
): readonly [string, string, string, string] {
  return ['users', uid, 'atlasBookmarks', slot];
}

/** Human-readable path — for logs/tests only. Don't pass to `doc(db, …)`. */
export function bookmarkDocPath(uid: string, slot: BookmarkSlot): string {
  return `/${bookmarkDocSegments(uid, slot).join('/')}`;
}

/** Zoom is clamped to keep OrbitControls inside a numerically-safe range. */
export const ZOOM_MIN = 0.05;
export const ZOOM_MAX = 100;

function isFiniteVec3(v: unknown): v is [number, number, number] {
  return (
    Array.isArray(v) &&
    v.length === 3 &&
    v.every((n) => typeof n === 'number' && Number.isFinite(n))
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

export function normalizeBookmark(raw: unknown): CameraBookmark | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (!isFiniteVec3(r.position) || !isFiniteVec3(r.target)) return null;
  const zoomRaw = r.zoom;
  const rawZoom = typeof zoomRaw === 'number' && Number.isFinite(zoomRaw) ? zoomRaw : 1;
  return {
    position: [r.position[0], r.position[1], r.position[2]],
    target: [r.target[0], r.target[1], r.target[2]],
    zoom: clamp(rawZoom, ZOOM_MIN, ZOOM_MAX),
  };
}
